//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import { AppBanner, Button, ButtonStyle } from '@cmp/common';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { ProductsList } from './components';
import { Header } from '@cmp/common';
import {
  FilterSelector,
  SearchInput,
  Paginate,
  ViewRecommendationsButton,
} from '@cmp/common';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import {
  Products as ProductsRequest,
  FeaturedCollections as FeaturedCollectionsRequest,
} from '@api/endpoints/get/products';
import {
  handleGtmEventDebounced,
  handleGtmEvent,
  DEBOUNCE_DELAY_VALUE_MS,
} from '@helpers/gtm';
//------------------------------------------------------------------------------
// Assets ----------------------------------------------------------------------
import { searchIconUrl } from '@cmp/images';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { shopifyFormatter, responseFormatter } from '@helpers/formatter';
import { ProductsSortingOptions } from '@helpers/constants/products';
import { getValue } from '@helpers/localStorage';
//------------------------------------------------------------------------------
// Constants -------------------------------------------------------------------
import {
  UserStorageKey,
  refCode,
  IsAdminApproved,
} from '@helpers/constants/localStorage';
import { TopCategories } from '@helpers/constants/products';
import { withTranslation } from 'react-i18next';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import Select from 'react-select';
//------------------------------------------------------------------------------
// Contexts -------------------------------------------------------------------
import ProductContext from '@context/product/ProductContext';
import { FilterGroupBlock, FilterGroups } from '../filters/filter';
import { FilterContext } from '../filters/context.jsx';
import { filterMap } from '../filters/filterData.js';
import { ShopByCategory } from '../filters/shopByCategory.jsx';
import { FilterButton } from '../filters/icons.jsx';
import { ClearFilterTags } from '../filters/filter';
//------------------------------------------------------------------------------
import FocusTrap from 'focus-trap-react';
import { Modal } from '@cmp/common';
import debounce from 'lodash/debounce';

// React Class -----------------------------------------------------------------
class Products extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchValue:
        new URLSearchParams(window.location.search).get('search') || '',
      sortBy: ProductsSortingOptions.find((x) => x.default),
      selectedCollectionId: '',
      selectedCollectionHandle: 'all-products',
      selectedCollectionTitle: '',
      collectionType: null,
      paginatedUrl: null,
      toggle: false,
      page: 1,
      lastEnabledFilters: [],
      mobileFilterModalOpen: false,
    };
    this.tagComponent = this.tagComponent.bind(this);
    this.handleSearchInputDebounced = debounce(
      this.handleSearchInputDebounced.bind(this),
      DEBOUNCE_DELAY_VALUE_MS
    );
  }

  handleSearchInputDebounced(func) {
    func();
  }

  providerCodeComponent() {
    const user = getValue(UserStorageKey, true);
    const isAdminApproved = !!getValue(IsAdminApproved, true);
    const referralCode = getValue(refCode);
    const profCodeEnabled = isAdminApproved;
    const { location, history, t } = this.props;

    return (
      <div>
        {profCodeEnabled && (
          <Button
            onClick={() => {
              history.push({
                pathname: '/recommendation/professional-code-share',
                state: { from: location, modal: true },
              });
            }}
            className={classNames(styles.Button, styles.customButton)}
            style={ButtonStyle.Secondary}
          >
            <span>{t('header_ShareYourCode', { code: referralCode })}</span>
          </Button>
        )}
      </div>
    );
  }

  headerComponent() {
    const { t } = this.props;
    const {
      featureFlags: { usePLPFiltering },
    } = this.context;

    return (
      <header className={styles.Header}>
        <h1 className={styles.Title}>{t('dashboard_SolutionsForPatients')}</h1>
        <span className={classNames(styles.Subtitle, styles.ProductsSubtitle)}>
          {t('dashboard_SolutionsForPatientsSubtitle')}
        </span>
        <div className={styles.buttonBlock}>
          <span
            className={classNames(styles.Subtitle, styles.DashboardSubtitle)}
          >
            <Button
              to="/recommendations"
              className={classNames(styles.Button, styles.customButton)}
              style={ButtonStyle.Secondary}
            >
              {t('dashboard_Dashboard')}
            </Button>
          </span>
          <span
            className={classNames(styles.Subtitle, styles.DashboardSubtitle)}
          >
            {this.providerCodeComponent()}
          </span>
        </div>
      </header>
    );
  }

  refreshCollection({
    selectedCollectionId,
    selectedCollectionHandle,
    selectedCollectionTitle,
  }) {
    this.setState(
      {
        paginatedUrl: null,
        selectedCollectionId,
        selectedCollectionHandle,
        selectedCollectionTitle,
      },
      this.refetch
    );
  }

  tagComponent(collection) {
    const { handle = '', id = 0 } = collection;
    const { t, i18n } = this.props;

    const makeNormHandle = (rawHandle = '') => {
      return String(rawHandle).toLowerCase().replace('-', '_');
    };
    const normHandle = makeNormHandle(handle);

    const { selectedCollectionId } = this.state;
    const isSelected =
      selectedCollectionId === collection.id ||
      (selectedCollectionId === '' &&
        TopCategories.findIndex((el) => el === collection.handle) === 0);

    const componentClasses = classNames(styles.Tag, {
      [styles.Selected]: isSelected,
    });

    const DASHBOARD_CATEGORIES_KEY_PREFIX = 'dashboard_Categories_';
    const normKey = `${DASHBOARD_CATEGORIES_KEY_PREFIX}${normHandle}`;
    const tKeyExists = i18n.exists(normKey);
    const tValueExists = t(normKey);

    if (!tKeyExists || !tValueExists) {
      return null;
    }

    return (
      <div
        className={componentClasses}
        key={collection.id}
        onClick={() => {
          this.setState(
            {
              paginatedUrl: null,
              selectedCollectionId: id,
              selectedCollectionHandle: handle,
              collectionType:
                collection.handle === 'all-products' ? 'smart' : null,
            },
            this.refetch
          );
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            this.setState(
              {
                paginatedUrl: null,
                selectedCollectionId: isSelected ? null : collection.id,
              },
              this.refetch
            );
          }
        }}
      >
        <h2 className={styles.TagText} tabIndex="0">
          {t(normKey)}
        </h2>
      </div>
    );
  }

  collectionsComponent() {
    const { selectedCollectionHandle, selectedCollectionTitle } = this.state;
    const selectStyles = {
      menu: (base) => ({
        ...base,
        zIndex: 100,
      }),
      control: (provided, state) =>
        state.selectProps.value.length === 0
          ? {
              ...provided,
              boxShadow: '0 0 0 .1px red !important',
              borderColor: 'red !important',
            }
          : provided,
    };
    return (
      <FeaturedCollectionsRequest stateIndicatorComponent={null}>
        {({ data }) => {
          if (!data) return '';
          let { custom_collections, smart_collections } = data;
          if (
            smart_collections.length > 0 &&
            smart_collections[0].handle === 'all-products'
          ) {
            custom_collections = [smart_collections[0], ...custom_collections];
          }
          let preferred_collections = [];
          TopCategories.forEach((handle) => {
            custom_collections.forEach((collection) => {
              if (collection.handle === handle) {
                if (collection.handle === 'best-sellers')
                  collection.title = 'more';
                collection.title = collection.title.toUpperCase();
                preferred_collections.push(collection);
              }
            });
          });
          custom_collections = preferred_collections;
          const options = shopifyFormatter
            .collections(custom_collections)
            .selectOptions();
          const tabsName = [];
          custom_collections.forEach((element) => {
            tabsName.push({ label: element.title, value: element.id });
          });

          const currentCollection =
            custom_collections.find(
              (collection) => collection.handle === selectedCollectionHandle
            ) || {};

          if (selectedCollectionTitle !== currentCollection.title) {
            this.setState({
              selectedCollectionTitle: currentCollection.title,
            });
          }

          const handleDropdown =
            (labelFirst = '') =>
            (event) => {
              const { label: labelCurrent = '', value } = event;
              const normValue = labelFirst !== labelCurrent ? value : '';
              this.refreshCollection({
                selectedCollectionId: normValue,
                selectedCollectionHandle: labelCurrent,
              });
            };

          const [first = {}] = tabsName;
          const { label: labelFirst = '' } = first;
          const DropdownPlaceholder = () => {
            return <div>{labelFirst}</div>;
          };

          return (
            <>
              <div className={styles.Tags}>
                {custom_collections &&
                  custom_collections.map(this.tagComponent)}
              </div>

              <div className={styles.dropdownSort}>
                <Select
                  className={styles.dropdown}
                  placeholder={<DropdownPlaceholder />}
                  options={tabsName}
                  isSearchable={true}
                  textFieldProps={{
                    label: 'Label',
                    InputLabelProps: {
                      shrink: true,
                    },
                  }}
                  onChange={handleDropdown(labelFirst)}
                />
              </div>
            </>
          );
        }}
      </FeaturedCollectionsRequest>
    );
  }

  listHeaderComponent(products = [], collects, total, loading) {
    const { t, i18n } = this.props;
    const { sortBy, searchValue, mobileFilterModalOpen } = this.state;
    const {
      featureFlags: { usePLPFiltering },
    } = this.context;
    return (
      <div className={styles.ListHeader}>
        <SearchInput
          value={searchValue}
          placeholder={t('dashboard_SearchFor')}
          icon={searchIconUrl(i18n)}
          onChange={(searchValue) => {
            this.setState({ searchValue }, () => {
              if (this.searchTimeout) clearTimeout(this.searchTimeout);
              this.searchTimeout = setTimeout(
                this.historyRefetch,
                searchValue.length > 0 ? 600 : 0
              );
              if (!!searchValue) {
                this.handleSearchInputDebounced(() => {
                  handleGtmEvent('search', {
                    eventParameters: {
                      omnibus: true,
                      action: 'full search',
                      search_term: searchValue,
                      search_result_count: this.state.products?.length,
                      search_location: 'plp',
                    },
                  });
                });
              }
            });
          }}
        />
        {!usePLPFiltering && this.collectionsComponent()}
        <div className={styles.Filter}>
          {!loading &&
            products &&
            collects &&
            (usePLPFiltering ? (
              <>
                <div className={styles.FilterMobile}>
                  <FilterButton
                    onClick={() =>
                      this.setState({ mobileFilterModalOpen: true })
                    }
                  />
                  {mobileFilterModalOpen ? (
                    <FocusTrap>
                      <Modal.Base
                        className={styles.MobileFilterGroupBlock}
                        baseModalClass={styles.MobileFilterGroupBlockModalClass}
                        onClose={() =>
                          this.setState({ mobileFilterModalOpen: false })
                        }
                      >
                        <>
                          <section className={styles.MobileFilterContainer}>
                            <h1>{t('dashboard_Filter_Text')}</h1>
                            <ClearFilterTags />
                            <div className={styles.ProductFilterDivider} />
                            <FilterGroupBlock />
                          </section>
                          <button
                            className={styles.BackToProductList}
                            onClick={() =>
                              this.setState({ mobileFilterModalOpen: false })
                            }
                          >
                            {t('dashboard_Filter_BackToProductList')}
                          </button>
                        </>
                      </Modal.Base>
                    </FocusTrap>
                  ) : null}
                  <h3 className={styles.ProductCount}>
                    {products.length} {t('of_text')} {total}{' '}
                    {t('dashboard_Products')}
                  </h3>
                </div>
                <div className={styles.MobileClearFilters}>
                  <ClearFilterTags />
                </div>
              </>
            ) : (
              <>
                <h3 className={styles.ProductCount}>
                  {products.length} {t('of_text')} {total}{' '}
                  {t('dashboard_Products')}
                </h3>
              </>
            ))}
          {!usePLPFiltering && (
            <SearchInput
              value={searchValue}
              placeholder={t('dashboard_SearchFor')}
              icon={searchIconUrl(i18n)}
              onChange={(searchValue) => {
                this.setState(
                  { searchValue, paginatedUrl: null, page: 1 },
                  () => {
                    history.pushState(
                      searchValue,
                      t('dashboard_Products'),
                      `?search=${searchValue}`
                    );
                    if (this.searchTimeout) clearTimeout(this.searchTimeout);
                    this.searchTimeout = setTimeout(
                      this.refetch,
                      searchValue.length > 0 ? 600 : 0
                    );
                    if (!!searchValue) {
                      this.handleSearchInputDebounced(() => {
                        handleGtmEvent('search', {
                          eventParameters: {
                            omnibus: true,
                            action: 'full search',
                            search_term: searchValue,
                            search_result_count: products.length,
                            search_location: 'plp',
                          },
                        });
                      });
                    }
                  }
                );
              }}
            />
          )}
        </div>
      </div>
    );
  }

  handlePageChange({ newPage = '', baseUrl = '' }) {
    const PAGE_PARAM = /(page=\d+)/;

    this.setState(() => {
      if (baseUrl && baseUrl.includes('page=')) {
        const futuredPageNumberPaginatedUrlUrl = baseUrl.replace(
          PAGE_PARAM,
          `page=${newPage}`
        );
        return {
          page: newPage,
          paginatedUrl: futuredPageNumberPaginatedUrlUrl,
        };
      }
    }, this.refetch);
  }

  listComponent(context, getEnabledFilters) {
    const {
      sortBy,
      searchValue,
      selectedCollectionId,
      selectedCollectionTitle,
      paginatedUrl,
      collectionType,
      page,
    } = this.state;
    const {
      products,
      setProducts,
      collects,
      setCollects,
      total,
      setTotal,
      loading,
      setLoading,
    } = context;
    const {
      featureFlags: { usePLPFiltering },
    } = this.context;
    const PRODUCTS_PER_PAGE = 16;

    return (
      <div className={styles.List}>
        {this.listHeaderComponent(products, collects, total, loading)}
        <ProductsRequest
          params={
            !paginatedUrl && {
              title: searchValue,
              sort: sortBy.value,
              ...(usePLPFiltering
                ? {
                    tags: getEnabledFilters()
                      .map((filter) => filterMap[filter.key])
                      .join(','),
                  }
                : {}),
              ...(collectionType === 'smart'
                ? { smart_collection_id: selectedCollectionId }
                : { collection_id: selectedCollectionId }),
              limit: PRODUCTS_PER_PAGE,
            }
          }
          customUrl={paginatedUrl}
          onComplete={(data) => {
            if (data.products) {
              this.setState({ products: data.products });
              setProducts(data.products);
            }
            if (data.total) {
              setTotal(data.total);
            }
            setLoading(false);
          }}
        >
          {({ data, headers, refetch, loading }) => {
            this.refetch = refetch;

            const { products, collects, total } = data || {};

            const enabledFilters = getEnabledFilters();
            const paginationUrls = responseFormatter
              .headers(headers)
              .pagination();

            if (products) {
              products.sort(
                (a, b) => collects.indexOf(a.id) - collects.indexOf(b.id)
              );
            }

            const numberOfPages =
              searchValue ||
              (usePLPFiltering &&
                products &&
                !!enabledFilters &&
                enabledFilters.length)
                ? Math.ceil(products.length / PRODUCTS_PER_PAGE)
                : Math.ceil(total / PRODUCTS_PER_PAGE);

            return (
              <>
                {usePLPFiltering ? (
                  <div className={styles.FilterDividerSection}>
                    <FilterGroups />
                    <ProductsList
                      listName={selectedCollectionTitle}
                      listId={selectedCollectionId}
                      products={products}
                      className={styles.ProductsList}
                      onAdd={() => undefined}
                    />
                  </div>
                ) : (
                  <ProductsList
                    listName={selectedCollectionTitle}
                    listId={selectedCollectionId}
                    products={products}
                    className={styles.ProductsList}
                    onAdd={() => undefined}
                  />
                )}
                <Paginate
                  onPageNumber={(newPage) => {
                    const paginatedUrl =
                      paginationUrls?.nextUrl || paginationUrls?.previousUrl;

                    this.handlePageChange({
                      newPage,
                      baseUrl: paginatedUrl,
                    });
                  }}
                  onNext={
                    paginationUrls.nextUrl &&
                    (() =>
                      this.setState((prevState) => {
                        const futurePage = prevState.page + 1;
                        return {
                          page: futurePage,
                          paginatedUrl: paginationUrls.nextUrl,
                        };
                      }, refetch))
                  }
                  onPrevious={
                    paginationUrls.previousUrl &&
                    (() =>
                      this.setState((prevState) => {
                        const futurePage = prevState.page - 1;
                        return {
                          page: futurePage,
                          paginatedUrl: paginationUrls.nextUrl,
                        };
                      }, refetch))
                  }
                  page={page}
                  numberOfPages={numberOfPages}
                />
              </>
            );
          }}
        </ProductsRequest>
      </div>
    );
  }

  render() {
    const { props, state, handleToggle } = this;
    const { t, history } = props;
    const {
      toggle,
      selectedCollectionHandle = '',
      selectedCollectionId = '',
      selectedCollectionTitle = '',
    } = state;
    const componentClasses = classNames(styles.Products);
    const {
      featureFlags: { usePLPFiltering },
    } = this.context;

    return (
      <ProductContext.Consumer>
        {(context) => {
          const { products = [] } = context;
          const eventParameters = {
            item_list_name: selectedCollectionTitle,
            item_list_id: selectedCollectionHandle,
          };
          const isInView = history?.location?.pathname === '/';
          if (products.length && isInView) {
            handleGtmEventDebounced('view_item_list', {
              context: this.context,
              products,
              eventParameters,
            });
          }

          return (
            <div className={componentClasses}>
              <Helmet>
                <title>{t('dashboard_Products')}</title>
              </Helmet>
              <Header toggle={toggle} onToggle={handleToggle} />
              <AppBanner></AppBanner>
              <FilterContext.Consumer>
                {({ getEnabledFilters }) => {
                  return (
                    <>
                      {this.headerComponent()}
                      {usePLPFiltering && <ShopByCategory />}
                      {this.listComponent(context, getEnabledFilters)}
                    </>
                  );
                }}
              </FilterContext.Consumer>
              <ViewRecommendationsButton className={styles.Recommendations} />
            </div>
          );
        }}
      </ProductContext.Consumer>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(Products);

Products.contextType = StoreConfigContext;
